import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { UpdateNotifier } from "@/components/UpdateNotifier";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import ImportPage from "@/pages/ImportPage";
import Planning from "@/pages/Planning";
import ProductionControl from "@/pages/ProductionControl";
import Projects from "@/pages/Projects";
import Registry from "@/pages/Registry";
import Resources from "@/pages/Resources";
import GroupConfig from "@/pages/GroupConfig";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import TechLayout from "@/pages/tech/TechLayout";
import TechHome from "@/pages/tech/TechHome";
import TechOrder from "@/pages/tech/TechOrder";
import TechAbsences from "@/pages/tech/TechAbsences";
import TechInventory from "@/pages/tech/TechInventory";
import TechProduction from "@/pages/tech/TechProduction";
import Warehouse from "@/pages/Warehouse";
import Fleet from "@/pages/Fleet";
import Accounting from "@/pages/Accounting";
import Personnel from "@/pages/Personnel";
import ManagementControl from "@/pages/ManagementControl";
import SetPassword from "@/pages/SetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import Documents from "@/pages/Documents";
import Security from "@/pages/Security";

const Guard = ({ area, children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground" data-testid="auth-loading">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (area === "staff" && user.role === "tecnico") return <Navigate to="/tecnico" replace />;
  if (area === "tech" && user.role !== "tecnico") return <Navigate to="/" replace />;
  return <DataProvider>{children}</DataProvider>;
};

const Perm = ({ perm, children }) => {
  const { can } = useAuth();
  return can(perm) ? children : <Navigate to="/" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/imposta-password" element={<SetPassword />} />
          <Route path="/password-dimenticata" element={<ForgotPassword />} />
          <Route path="/tecnico" element={<Guard area="tech"><TechLayout /></Guard>}>
            <Route index element={<TechHome />} />
            <Route path="ordine/:id" element={<TechOrder />} />
            <Route path="assenze" element={<TechAbsences />} />
            <Route path="inventario" element={<TechInventory />} />
            <Route path="produzione" element={<TechProduction />} />
          </Route>
          <Route path="/" element={<Guard area="staff"><AppShell /></Guard>}>
            <Route index element={<Dashboard />} />
            <Route path="ordini" element={<Orders />} />
            <Route path="ordini/:id" element={<OrderDetail />} />
            <Route path="importazione" element={<Perm perm="import:run"><ImportPage /></Perm>} />
            <Route path="pianificazione" element={<Planning />} />
            <Route path="produzione" element={<ProductionControl />} />
            <Route path="commesse" element={<Perm perm="commercial:read"><Projects /></Perm>} />
            <Route path="risorse" element={<Perm perm="hr:read"><Resources /></Perm>} />
            <Route path="magazzino" element={<Perm perm="inventory:read"><Warehouse /></Perm>} />
            <Route path="flotta" element={<Perm perm="fleet:read"><Fleet /></Perm>} />
            <Route path="amministrazione" element={<Perm perm="accounting:read"><Accounting /></Perm>} />
            <Route path="personale" element={<Perm perm="hr:read"><Personnel /></Perm>} />
            <Route path="documentale" element={<Perm perm="docs:read"><Documents /></Perm>} />
            <Route path="sicurezza" element={<Perm perm="group:manage"><Security /></Perm>} />
            <Route path="controllo-gestione" element={<Perm perm="finance:read"><ManagementControl /></Perm>} />
            <Route path="anagrafiche" element={<Registry />} />
            <Route path="gruppo" element={<Perm perm="group:manage"><GroupConfig /></Perm>} />
            <Route path="audit" element={<Perm perm="audit:read"><Audit /></Perm>} />
            <Route path="impostazioni" element={<Perm perm="group:manage"><Settings /></Perm>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <UpdateNotifier />
    </AuthProvider>
  );
}

export default App;
